import { createSlice } from '@reduxjs/toolkit';
import { TherapyState } from '../state.types';
import { TherapyAvailabilityNewI, TherapyListParam } from 'src/interfaces/therapyInterface';
import moment from 'moment';

const initialState: TherapyState = {
  getTherapyList: {
    isSuccess: false,
    statusCode: '',
    message: '',
    metadata: {
      page: 1,
      perPage: 10,
      pageCount: null,
      totalCount: null,
    },
    data: [],
  },
  TherapistDetail: {
    id: 0,
    fullName: '',
    address: '',
    email: '',
    phone: '',
    stateId: 0,
    cityId: 0,
    city: '',
    state: '',
    description: '',
    shortDescription: '',
    qualification: '',
    profilePicture: '',
    profilePictureThumbnail: '',
    rating: 0,
    therapistAnswers: [],
    gender: '',
  },
  TherapistDetailView: '',
  editTherapistDetailView: '',
  availability: [],
  currentAvailabilityId: '',
  currentAvailability: '',
  therapyQuestions: [],
  upcomingSessions: [],
  upcomingSessionsMetadata: {
    page: -1,
    perPage: -1,
    pageCount: -1,
    totalCount: -1,
  },
  pastSessions: [],
  pastSessionMetadata: {
    page: -1,
    perPage: -1,
    pageCount: -1,
    totalCount: -1,
  },
  myAvailability: '',
  completedOnboardingQuestions: '',
  intakeFormData: null,
  summaryUpload: false,
  summaryDownloadData: null,
};

const therapySlice = createSlice({
  name: 'therapy',
  initialState,
  reducers: {
    setGetTherapyList: (state, action) => {
      state.getTherapyList.data = action.payload;
    },
    setTherapistDetail: (state, action) => {
      state.TherapistDetail = action.payload;
    },
    setMetadata: (state, action) => {
      state.getTherapyList.metadata = action.payload;
    },
    createTherapist: (state, action) => {
      state.getTherapyList.data.unshift(action.payload);
    },
    setTherapistDetailView: (state, action) => {
      state.TherapistDetailView = action.payload;
    },
    deleteTherapy: (state, action) => {
      state.getTherapyList.data = state.getTherapyList.data.filter(
        (therapy: TherapyListParam) => therapy.id !== action.payload,
      );
    },
    deactivateActivateTherapist: (state, action) => {
      const value = state.getTherapyList.data.find(
        (c: TherapyListParam) => c.id === action.payload.therapistId,
      );
      if (value) {
        value.status = action.payload.status;
      }
      return state;
    },
    setEditTherapistDetailView: (state, action) => {
      state.editTherapistDetailView = action.payload;
    },
    updateTherapy(state, action) {
      state.getTherapyList.data = state.getTherapyList.data.map((therapy: TherapyListParam) => {
        if (therapy.id === action.payload) {
          therapy = action.payload;
        }
        return therapy;
      });
    },
    setTherapyAvailability: (state, action) => {
      state.availability = action.payload;
    },
    deleteTherapyProfilePicture: (state, action) => {
      const therapyData = state.getTherapyList.data.find(
        (therapy: TherapyListParam) => therapy.id === action.payload,
      );
      therapyData?.profilePicture === '';
    },
    setCurrentAvailabilityId: (state, action) => {
      state.currentAvailabilityId = action.payload;
    },
    setCurrentAvailability: (state, action) => {
      state.currentAvailability = action.payload;
    },
    addTherapyAvailability: (state, action) => {
      const newStartDate = moment(
        `${action.payload.startDate} ${action.payload.startHour}`,
        'YYYY-MM-DD HH:mm',
      );
      const findDiff = state.availability.filter(
        (av: any) =>
          newStartDate.diff(
            moment(`${av.startDate} ${av.startHour}`, 'YYYY-MM-DD HH:mm'),
            'minutes',
          ) > 0,
      );
      state.availability.splice(findDiff.length, 0, action.payload);
      state.myAvailability.splice(findDiff.length, 0, action.payload);
    },
    deleteTherapistAvailability: (state, action) => {
      const findAv = state.availability.find(
        (av: any) =>
          av.startDate === action.payload.startDate &&
          av.endDate === action.payload.endDate &&
          av.startHour === action.payload.startHour &&
          av.endHour === action.payload.endHour,
      );

      const findMyAv = state.myAvailability.find(
        (av: any) =>
          av.startDate === action.payload.startDate &&
          av.endDate === action.payload.endDate &&
          av.startHour === action.payload.startHour &&
          av.endHour === action.payload.endHour,
      );
      state.availability = state.availability.filter(
        (availability: TherapyAvailabilityNewI) => availability !== findAv,
      );
      state.myAvailability = state.myAvailability.filter(
        (availability: TherapyAvailabilityNewI) => availability !== findMyAv,
      );
    },
    setMyAvailability: (state, action) => {
      state.myAvailability = action.payload;
    },
    setTherapyQuestions: (state, action) => {
      state.therapyQuestions = action.payload;
    },
    setUpcomingSessions: (state, action) => {
      state.upcomingSessions = action.payload;
    },
    setUpcomingSessionsMetadata: (state, action) => {
      state.upcomingSessionsMetadata = action.payload;
    },
    setPastSessions: (state, action) => {
      state.pastSessions = action.payload;
    },
    setPastSessionsMetadata: (state, action) => {
      state.pastSessionMetadata = action.payload;
    },
    setCompletedOnboardingQuestions: (state, action) => {
      state.completedOnboardingQuestions = action.payload;
    },
    setIntakeFormDtaa: (state, action) => {
      state.intakeFormData = action.payload;
    },
    setSummaryUpload: (state, action) => {
      state.summaryUpload = action.payload;
    },
    setSummaryDownloadData: (state, action) => {
      state.summaryDownloadData = action.payload;
    },
  },
});

export const {
  setGetTherapyList,
  setTherapistDetail,
  createTherapist,
  setTherapistDetailView,
  deleteTherapy,
  deactivateActivateTherapist,
  setEditTherapistDetailView,
  updateTherapy,
  setTherapyAvailability,
  deleteTherapyProfilePicture,
  addTherapyAvailability,
  // addTherapyMyAvailability,
  setCurrentAvailability,
  setCurrentAvailabilityId,
  deleteTherapistAvailability,
  setMetadata,
  setMyAvailability,
  setTherapyQuestions,
  setUpcomingSessions,
  setUpcomingSessionsMetadata,
  setPastSessions,
  setPastSessionsMetadata,
  setCompletedOnboardingQuestions,
  setIntakeFormDtaa,
  setSummaryUpload,
  setSummaryDownloadData,
} = therapySlice.actions;

export default therapySlice.reducer;
